import { motion, useInView, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";

const EASE = [0.22, 1, 0.36, 1];

// Parallax image — gentle scroll-linked drift for depth (reduced-motion safe).
export const ParallaxImage = ({ src, alt = "", className = "", imgClassName = "", distance = 40, ...rest }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], reduce ? ["0px", "0px"] : [`-${distance}px`, `${distance}px`]);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ y }}
        className={`absolute -top-[8%] left-0 h-[116%] w-full object-cover ${imgClassName}`}
        {...rest}
      />
    </div>
  );
};

// Line-by-line masked reveal. Pass `lines` as an array of strings (or nodes).
export const MaskReveal = ({ lines, className = "", lineClassName = "", delay = 0, as = "h2", ...props }) => {
  const Tag = motion[as] || motion.h2;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  return (
    <Tag ref={ref} className={className} {...props}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden pb-[0.14em] -mb-[0.14em]">
          <motion.span
            className={`block ${lineClassName}`}
            initial={{ y: "110%" }}
            animate={inView ? { y: "0%" } : { y: "110%" }}
            transition={{ duration: 1.1, ease: EASE, delay: delay + i * 0.12 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

// Simple slow fade-up for content blocks.
export const FadeUp = ({ children, className = "", delay = 0, y = 28, ...props }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-8% 0px" }}
    transition={{ duration: 1.1, ease: EASE, delay }}
    {...props}
  >
    {children}
  </motion.div>
);

export const Line = ({ className = "", delay = 0 }) => (
  <motion.div
    className={`h-px bg-gaia-charcoal/20 origin-left ${className}`}
    initial={{ scaleX: 0 }}
    whileInView={{ scaleX: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 1.4, ease: EASE, delay }}
  />
);

export { EASE };
